/**
 * Pure tap-to-swap helpers for the DRAFT_PPR LineupEditor.
 *
 * The editor's swap model is bench-first: tap a bench card, the starter slots
 * its position could legally occupy highlight (FLEX accepts RB/WR/TE), then tap
 * a starter to complete the swap — the bench card takes the starter's slot and
 * the starter drops to the bench. Because every swap preserves the exact slot
 * counts (the incoming card is always eligible for the outgoing card's slot),
 * a lineup built purely from swaps can never violate the server's
 * LINEUP_SHAPE_INVALID rule.
 */

/** The 6 starter slot kinds (RB/WR repeat — 8 starters total). */
export type StarterSlot = 'QB' | 'RB' | 'WR' | 'TE' | 'FLEX' | 'K';

/** Positions eligible for each starter slot (mirrors the server's SLOT_ELIGIBLE). */
export const SLOT_ELIGIBLE: Record<StarterSlot, readonly string[]> = {
  QB: ['QB'],
  RB: ['RB'],
  WR: ['WR'],
  TE: ['TE'],
  K: ['K'],
  FLEX: ['RB', 'WR', 'TE'],
};

/** Render order for the 8 starter rows. */
export const STARTER_SLOT_ORDER: readonly StarterSlot[] = ['QB', 'RB', 'WR', 'TE', 'FLEX', 'K'];

/** True when a card of `position` may occupy `slot` (FLEX = RB/WR/TE). */
export function isPositionEligibleForSlot(position: string, slot: string): boolean {
  const eligible = SLOT_ELIGIBLE[slot as StarterSlot];
  return !!eligible && eligible.includes(position);
}

/**
 * The starter cardIds a bench card of `benchPosition` could legally replace
 * (i.e. the highlighted swap targets). Empty when nothing is eligible — e.g. a
 * bench RB against a lineup whose only open kinship is QB/K slots.
 */
export function eligibleSwapTargets(
  benchPosition: string,
  starters: readonly { cardId: string; slot: string }[]
): Set<string> {
  const targets = new Set<string>();
  for (const s of starters) {
    if (isPositionEligibleForSlot(benchPosition, s.slot)) targets.add(s.cardId);
  }
  return targets;
}

/**
 * Apply a bench→starter swap to a starters slot map (cardId → slot): the bench
 * card takes the starter's slot; the starter leaves the map (bench derives as
 * roster − starters). Returns a NEW map — the input is never mutated. Returns
 * null when the swap is not applicable (starter absent or bench already
 * starting), so callers can ignore stray taps.
 */
export function applySwap(
  slotByCard: ReadonlyMap<string, string>,
  benchCardId: string,
  starterCardId: string
): Map<string, string> | null {
  const slot = slotByCard.get(starterCardId);
  if (!slot || slotByCard.has(benchCardId)) return null;
  const next = new Map(slotByCard);
  next.delete(starterCardId);
  next.set(benchCardId, slot);
  return next;
}

/**
 * True when two starter maps field a different lineup (used for the editor's
 * dirty/save state). Slot moves among the same 8 starters count as dirty too —
 * the FLEX designation is part of the stored lineup.
 */
export function lineupDiffers(
  a: ReadonlyMap<string, string>,
  b: ReadonlyMap<string, string>
): boolean {
  if (a.size !== b.size) return true;
  for (const [cardId, slot] of a) {
    if (b.get(cardId) !== slot) return true;
  }
  return false;
}
